import type { HomeDeliverySetting } from 'models/ppoints/settings/HomeDeliverySetting';
import { queryClient } from 'utils/queryClient';
import { Aggregator } from '../../../../pb/aggregators/v1/aggregator_pb';
import { AVAILABLE_PROVIDERS, PackageProviderName, SettingKey } from '../../../data/ppoints/constants';
import { ShippingType } from '../../../features/orders/models/ShippingType';
import { ShopifyOrder } from '../../../features/orders/models/ShopifyOrder';
import OrderService from '../../../features/orders/services/OrderService';
import httpApi from '../../../hooks/api/httpApi';
import { Country, CountryWithProviders } from '../../../models/ppoints/Country';
import { CountryProviderShippingTypes } from '../../../models/ppoints/CountryProviderShippingTypes';
import { PackageProvider } from '../../../models/ppoints/PackageProvider';
import {
    EnabledShippingTypesSetting,
    ProviderToCountrySetting,
    SettingsToEcom,
} from '../../../models/ppoints/settings/SettingsToEcom';
import { ShippingRatesSettings } from '../../../models/ppoints/settings/ShippingRatesSettings';

/**
 * Redux Store has to be injected in index.tsx like this.
 * If the store was imported like usually, it would result in error.
 */
let store;
const injectStore = (_store) => {
    store = _store;
};

const getAll = async () => {
    const res = await httpApi.get<PackageProvider[]>('/api/package-providers');
    return res.getData();
};
const getAllCached = async (): Promise<PackageProvider[]> => {
    return await queryClient.ensureQueryData({
        queryKey: ['package-providers'],
        queryFn: getAll,
        staleTime: 10 * 60000,
        cacheTime: 10 * 60000,
    });
};

const getAvailableProvidersInAllOfTheCountries = (allCountries: CountryWithProviders[], countriesList: string[]) => {
    // we only need the countries whose names were passed in countriesList
    allCountries = allCountries.filter((country) =>
        countriesList.find((countryString) => countryString === country.name)
    );

    // Find first element of countriesList in allCountries
    let firstCountry = allCountries.find((country) => country.name === countriesList[0]);
    if (!firstCountry) {
        return [];
    }

    // Take the providers for that country
    let availableProviders = firstCountry.packageproviders;

    // Loop through rest of allCountries
    for (let i = 1; i < countriesList.length; i++) {
        // Find current country in allCountries
        let countryFound = allCountries.find((country) => country.name === countriesList[i]);
        if (!countryFound) {
            return [];
        }

        // Filter availableProviders: keep only providers which are also available in current country
        availableProviders = availableProviders.filter((provider) =>
            countryFound.packageproviders.find((provider2) => provider.name === provider2.name)
        );
    }

    return availableProviders;
};
// Home delivery types are `<provider>_hd` (plus the generic `home_delivery`); the zone page lists pickup points only
const isHomeDeliveryShippingType = (shippingType: ShippingType) =>
    shippingType === ShippingType.HOME_DELIVERY || shippingType.endsWith('_hd');

/**
 * Providers that ship to every country in `countriesList` (country names), judged by the pickup-point shipping types
 * enabled per country (`/api/available-shipping-types`) rather than by the activation list of `allCountries`
 * (`/api/countries`). A provider can be activated in a single country yet deliver to many (e.g. PPL: activated
 * in CZ only), so the activation list would drop it from the other countries' zones.
 * The provider objects are taken from `allCountries`, wherever the provider is listed.
 */
const getProvidersShippingToAllOfTheCountries = (
    allCountries: CountryWithProviders[],
    shippingTypes: CountryProviderShippingTypes,
    countriesList: string[]
): PackageProvider[] => {
    if (!countriesList.length) return [];

    const codes = countriesList.map((name) =>
        allCountries.find((country) => country.name.toLowerCase() === name.toLowerCase())?.country_code?.toLowerCase()
    );
    if (codes.some((code) => !code)) return [];

    const typesByCode = Object.fromEntries(
        Object.entries(shippingTypes).map(([code, providers]) => [code.toLowerCase(), providers])
    );

    const knownProviders = new Map<PackageProviderName, PackageProvider>();
    allCountries.forEach((country) =>
        country.packageproviders.forEach((provider) => {
            if (!knownProviders.has(provider.name)) knownProviders.set(provider.name, provider);
        })
    );

    return [...knownProviders.values()].filter((provider) =>
        codes.every((code) => typesByCode[code]?.[provider.name]?.some((type) => !isHomeDeliveryShippingType(type)))
    );
};

const getAvailableProvidersInAnyOfTheCountries = (allCountries: CountryWithProviders[], countriesList: string[]) => {
    let availableProviders: PackageProvider[] = [];
    for (let i = 0; i < countriesList.length; i++) {
        let countryFound = allCountries.find((country) => countriesList.includes(country.name));
        if (!countryFound) {
            continue;
        }
        availableProviders = [...availableProviders, ...countryFound.packageproviders];
    }
    /* remove duplicates */
    availableProviders = availableProviders.filter(
        (provider, index) => availableProviders.findIndex((item) => item.name === provider.name) === index
    );
    return availableProviders;
};

const getById = async (id: number): Promise<PackageProvider | null> => {
    let all = await getAllCached();
    return all.find((provider) => provider.id === id) || null;
};

const getByName = async (name: PackageProviderName): Promise<PackageProvider | null> => {
    let all = await getAllCached();
    return all.find((provider) => provider.name === name) || null;
};

const isProviderEnabled = async (ecomId: number, name: string): Promise<boolean> => {
    const settings = store?.getState().settings.settings;
    if (!settings) return false;
    let providers = await getAllCached();
    const shippingZoneSetting = settings[SettingKey.zones_and_providers];

    // TODO group MPL providers
    switch (name) {
        case PackageProviderName.MPL_MOL:
        case PackageProviderName.MPL_COOP:
        case PackageProviderName.MPL_OFFICE:
        case PackageProviderName.MPL_MEDIAMARKT:
            name = PackageProviderName.MPL_PACKAGE_POINT;
            break;
    }
    if (name === PackageProviderName.MPL_DELIVERY) {
        return false;
    }

    const provider = providers.find((provider) => provider.name === name);
    if (!provider) return false;

    if (shippingZoneSetting && Object.keys(shippingZoneSetting).length > 0) {
        for (let zoneId of Object.keys(shippingZoneSetting)) {
            if (!!shippingZoneSetting[zoneId] && shippingZoneSetting[zoneId].includes(provider.id)) {
                return true;
            }
        }
    }

    return false;
};

const getProviderStates = async (ecomId: number | null): Promise<Record<PackageProviderName, boolean>> => {
    if (!ecomId) return Promise.reject();
    let providerStates = {} as Record<PackageProviderName, boolean>;
    for (let name in PackageProviderName) {
        providerStates[PackageProviderName[name]] = await isProviderEnabled(ecomId, PackageProviderName[name]);
    }
    return providerStates;
};

const getCountriesForProvider = async (providerId: number) => {
    const res = await httpApi.get<Country[]>(`/api/countries-for-provider/${providerId}`);
    const res2 = res.getData();
    return res2;
};

const getDisplayName = (providerName: PackageProviderName) => {
    switch (providerName) {
        case PackageProviderName.FOXPOST:
            return 'FOXPOST - Packeta Group';
        case PackageProviderName.MPL_PACKAGE_POINT:
            return 'Magyar Posta (MPL)';
        case PackageProviderName.MPL_DELIVERY:
            return 'MPL Delivery';
        case PackageProviderName.MPL_MOL:
            return 'MPL MOL';
        case PackageProviderName.MPL_COOP:
            return 'MPL COOP';
        case PackageProviderName.MPL_MEDIAMARKT:
            return 'MPL MediaMarkt';
        case PackageProviderName.MPL_OFFICE:
            return 'MPL Office';
        case PackageProviderName.GLS:
            return 'GLS';
        case PackageProviderName.GLS_GLOBAL:
            return 'GLS Global';
        case PackageProviderName.DPD:
            return 'DPD';
        case PackageProviderName.SPRINTER:
            return 'Sprinter / Pick Pack Pont';
        case PackageProviderName.PACKETA:
            return 'Packeta';
        case PackageProviderName.TOURMIX:
            return 'TOURMIX';
        case PackageProviderName.FAN_COURIER:
            return 'FAN Courier';
        case PackageProviderName.FAN_COURIER_INTERNATIONAL:
            return 'FAN Courier International';
        case PackageProviderName.INPOST:
            return 'InPost';
        case PackageProviderName.POSTASK:
            return 'Slovenská pošta';
        case PackageProviderName.BPOST:
            return 'BPost';
        case PackageProviderName.CORREOS:
            return 'Correos';
        case PackageProviderName.OMNIVA:
            return 'Omniva';
        case PackageProviderName.CARGUS:
            return 'Cargus';
        case PackageProviderName.POCZTA_POLSKA:
            return 'Poczta Polska';
        case PackageProviderName.ONE_BY_ALLEGRO_WEDO:
            return 'One by Allegro';
        case PackageProviderName.GLS_POLAND:
            return 'GLS Poland';
        case PackageProviderName.DPD_POLAND:
            return 'DPD Poland';
        case PackageProviderName.ECONT:
            return 'Econt';
        case PackageProviderName.ECONT_ROMANIA:
            return 'Econt Romania';
        case PackageProviderName.DPD_GLOBAL:
            return 'DPD Global';
        case PackageProviderName.DPD_GREECE:
        case PackageProviderName.DPD_ITALY:
        case PackageProviderName.DPD_SLOVENIA:
        case PackageProviderName.DPD_GB:
        case PackageProviderName.DPD_LATVIA:
        case PackageProviderName.DPD_PORTUGAL:
        case PackageProviderName.DPD_SLOVAKIA:
        case PackageProviderName.DPD_BULGARIA:
        case PackageProviderName.DPD_SWEDEN:
        case PackageProviderName.DPD_GERMANY:
        case PackageProviderName.DPD_CZECH_REPUBLIC:
        case PackageProviderName.DPD_LUXEMBOURG:
        case PackageProviderName.DPD_SPAIN:
        case PackageProviderName.DPD_SWITZERLAND:
        case PackageProviderName.DPD_AUSTRIA:
        case PackageProviderName.DPD_BELGIUM:
        case PackageProviderName.DPD_NETHERLANDS:
        case PackageProviderName.DPD_ROMANIA:
        case PackageProviderName.DPD_ESTONIA:
        case PackageProviderName.DPD_FRANCE:
        case PackageProviderName.DPD_LITHUANIA:
            return 'DPD';
        case PackageProviderName.GLS_FI:
        case PackageProviderName.GLS_IT:
        case PackageProviderName.GLS_BG:
        case PackageProviderName.GLS_BE:
        case PackageProviderName.GLS_FR:
        case PackageProviderName.GLS_NL:
        case PackageProviderName.GLS_LU:
        case PackageProviderName.GLS_DE:
        case PackageProviderName.GLS_ES:
        case PackageProviderName.GLS_PT:
        case PackageProviderName.GLS_AT:
        case PackageProviderName.GLS_GR:
        case PackageProviderName.GLS_DK:
            return 'GLS';
        case PackageProviderName.PAXY:
            return 'Paxy';
        case PackageProviderName.TOPTRANS:
            return 'Toptrans';
        case PackageProviderName.CESKA_POSTA:
            return 'Česká pošta';
        case PackageProviderName.EXPRESS_ONE:
            return 'Express One';
        case PackageProviderName.CITY_EXPRESS:
            return 'City Express';
        case PackageProviderName.POSTA_SRBIJE:
            return 'Pošta Srbije';
        case PackageProviderName.HRVATSKA_POSTA:
            return 'Hrvatska pošta';
        case PackageProviderName.POST_LUXEMBOURG:
            return 'POST Luxembourg';
        case PackageProviderName.CTT:
            return 'CTT';
        case PackageProviderName.OVERSEAS_EXPRESS:
            return 'Overseas Express';
        case PackageProviderName.UNISEND:
            return 'Unisend';
        case PackageProviderName.POSTA_BEZ_HRANIC:
            return 'Pošta bez hraníc';
        case PackageProviderName.SENDCLOUD:
            return 'Sendcloud';
        case PackageProviderName.SEVEN_SENDERS:
            return 'Seven Senders';
        case PackageProviderName.BUDBEE:
            return 'Budbee';
        case PackageProviderName.SPRING_GDS:
            return 'Spring GDS';
        case PackageProviderName.SHIPPYPRO:
            return 'ShippyPro';
        case PackageProviderName.INSTABOX:
            return 'Instabox';
        case PackageProviderName.HELTHJEM:
            return 'Helthjem';
        case PackageProviderName.DB_SCHENKER:
            return 'DB Schenker';
        case PackageProviderName.POSTI:
            return 'Posti';
        case PackageProviderName.BAL_KOVNA:
            return 'Balíkovna';
        case PackageProviderName.BOX_NOW:
            return 'Box Now';
        case PackageProviderName.DHL:
            return 'DHL';
        case PackageProviderName.ECOLET:
            return 'Ecolet';
        case PackageProviderName.FEDEX:
            return 'FedEx';
        case PackageProviderName.MATKAHUOLTO:
            return 'Matkahuolto';
        case PackageProviderName.ORLEN_PACZKA:
            return 'ORLEN Paczka';
        case PackageProviderName.PPL:
            return 'PPL';
        case PackageProviderName.SPS_SLOVAK_PARCEL_SERVICE:
            return 'SPS';
        case PackageProviderName.UPS:
            return 'UPS';
        case PackageProviderName.VENIPAK:
            return 'Venipak';
        case PackageProviderName.WOOT_ROMANIA:
            return 'Woot';
        case PackageProviderName.YBOX24:
            return 'ybox24';
        case PackageProviderName.AUSTRALIA_POST:
            return 'Australia Post';
        case PackageProviderName.HUBBED_PARCELPOINT:
            return 'HUBBED / PARCELPOINT';
        case PackageProviderName.ARAMEX_AUSTRALIA:
            return 'Aramex Australia – Parcel Connect';
        case PackageProviderName.COURIERS_PLEASE:
            return 'CouriersPlease';
        case PackageProviderName.SF_EXPRESS:
            return 'SF Express';
        case PackageProviderName.HONGKONG_POST:
            return 'Hongkong Post / iPostal';
        case PackageProviderName.ALFRED24:
            return 'alfred24';
        case PackageProviderName.CAINIAO:
            return 'Cainiao';
        case PackageProviderName.PICK:
            return 'PICK';
        case PackageProviderName.SINGPOST:
            return 'SingPost / POPStation / Parcel Santa';
        case PackageProviderName.NINJA_VAN:
            return 'Ninja Van';
        case PackageProviderName.SHOPEE_SPX:
            return 'Shopee / SPX';
        case PackageProviderName.HIVE_BOX:
            return 'Hive Box';
        case PackageProviderName.JD_LOGISTICS:
            return 'JD Logistics';
        case PackageProviderName.CHINA_POST_EMS:
            return 'China Post / EMS';
        case PackageProviderName.SEVEN_ELEVEN:
            return '7-Eleven';
        case PackageProviderName.FAMILYMART:
            return 'FamilyMart / FamiPort';
        case PackageProviderName.HI_LIFE:
            return 'Hi-Life';
        case PackageProviderName.OK_MART:
            return 'OK mart';
        case PackageProviderName.CHUNGHWA_POST:
            return 'Chunghwa Post – i-Mailbox';
        case PackageProviderName.PACKCITY_JAPAN:
            return 'Packcity Japan / PUDO';
        case PackageProviderName.YAMATO_TRANSPORT:
            return 'Yamato Transport';
        case PackageProviderName.JAPAN_POST:
            return 'Japan Post';
        case PackageProviderName.SAGAWA_EXPRESS:
            return 'Sagawa Express';
        case PackageProviderName.GS25:
            return 'GS25 / GS Postbox';
        case PackageProviderName.CUPOST:
            return 'CU / CUpost';
        case PackageProviderName.CJ_LOGISTICS:
            return 'CJ Logistics';
        case PackageProviderName.KOREA_POST:
            return 'Korea Post (ePost)';
        case PackageProviderName.CHILEXPRESS:
            return 'Chilexpress';
        case PackageProviderName.BLUE_EXPRESS:
            return 'Blue Express';
        case PackageProviderName.STARKEN:
            return 'Starken';
        case PackageProviderName.CORREOS_DE_CHILE:
            return 'Correos de Chile';
        case PackageProviderName.PICKIT:
            return 'Pickit';
        case PackageProviderName.OCA:
            return 'OCA';
        case PackageProviderName.CORREO_ARGENTINO:
            return 'Correo Argentino';
        case PackageProviderName.ANDREANI:
            return 'Andreani / HOP';
        case PackageProviderName.URBANO:
            return 'Urbano';
        case PackageProviderName.ENVIOPACK:
            return 'Envíopack';
        case PackageProviderName.JADLOG:
            return 'Jadlog';
        case PackageProviderName.CORREIOS:
            return 'Correios';
        case PackageProviderName.CLIQUE_RETIRE:
            return 'Clique Retire / Grupo Clique';
        case PackageProviderName.PEGAKI:
            return 'Pegaki';
        case PackageProviderName.COORDINADORA:
            return 'Coordinadora / Droop';
        case PackageProviderName.SERVIENTREGA:
            return 'Servientrega';
        case PackageProviderName.INTERRAPIDISIMO:
            return 'Interrapidísimo';
        case PackageProviderName.PARGO:
            return 'Pargo';
        case PackageProviderName.THE_COURIER_GUY:
            return 'The Courier Guy / pudo';
        case PackageProviderName.PAXI:
            return 'PAXI';
        case PackageProviderName.BOB_GO:
            return 'Bob Box / Bob Go';
        case PackageProviderName.POSTNET:
            return 'PostNet';
        case PackageProviderName.CANADA_POST:
            return 'Canada Post';
        case PackageProviderName.PUROLATOR:
            return 'Purolator';
        case PackageProviderName.FEDEX_HOLD_AT_LOCATION:
            return 'FedEx (Hold at Location)';
        case PackageProviderName.AMAZON_LOCKER:
            return 'Amazon Locker';
        case PackageProviderName.USPS:
            return 'USPS';
        case PackageProviderName.KVIKK_GLS:
            return 'Kvikk GLS';
        case PackageProviderName.KVIKK_DPD:
            return 'Kvikk DPD';
        case PackageProviderName.KVIKK_PACKETA:
            return 'Kvikk Packeta';
        case PackageProviderName.KVIKK_MPL:
            return 'Kvikk MPL';
        case PackageProviderName.KVIKK_FOXPOST:
            return 'Kvikk Foxpost';

        default:
            return (providerName as string).toUpperCase();
    }
};

const getAggregatorDisplayName = (aggregatorName: Aggregator) => {
    switch (aggregatorName) {
        case Aggregator.KVIKK:
            return 'Kvikk';
        default:
            return Aggregator[aggregatorName] ?? '';
    }
};

const getProviderNameFromOrder = (order: ShopifyOrder) => {
    let shippingType = OrderService.getShippingType(order);
    if (!shippingType) {
        return null;
    }
    switch (shippingType) {
        case ShippingType.FOXPOST:
            return PackageProviderName.FOXPOST;
        case ShippingType.MPL_CS:
            return PackageProviderName.MPL_PACKAGE_POINT;
        case ShippingType.MPL_MOL:
            return PackageProviderName.MPL_MOL;
        case ShippingType.MPL_COOP:
            return PackageProviderName.MPL_COOP;
        case ShippingType.MPL_MEDIAMARKT:
            return PackageProviderName.MPL_MEDIAMARKT;
        case ShippingType.MPL_OFFICE:
            return PackageProviderName.MPL_OFFICE;
        case ShippingType.GLS:
            return PackageProviderName.GLS;
        case ShippingType.DPD:
            return PackageProviderName.DPD;
        default:
            return null;
    }
};

const getCountries = async () => {
    const res = await httpApi.get<CountryWithProviders[]>('/api/countries');
    return res.getData();
};

const getAvailableShippingTypes = async () => {
    const res = await httpApi.get<CountryProviderShippingTypes>('/api/available-shipping-types');
    return res.getData();
};

export const getAvailableProviderNames = async () => {
    let res = await httpApi.get<PackageProvider[]>(`/api/package-providers`);
    let data = res.getData();

    const providersEnabled: PackageProviderName[] = data
        .filter((provider) => provider.enabled)
        .map((provider) => provider.name);
    return providersEnabled;
};

/**
 * Returns count of enabled shipping types for package provider.
 * @param providerName
 * @param enabledShippingTypes
 */
const countEnabledShippingTypes = (
    providerName: PackageProviderName,
    enabledShippingTypes?: EnabledShippingTypesSetting
): number => {
    if (!enabledShippingTypes) {
        const settings = store.getState().settings.settings;
        enabledShippingTypes = settings[SettingKey.enabled_shipping_types];
    }
    const enabledShippingTypesByProvider = store.getState().enabledShippingTypes.enabledShippingTypes;

    let countTotal = 0;
    for (let countryCode in enabledShippingTypes) {
        let shippingTypesInCountry = enabledShippingTypes[countryCode]?.[providerName];
        if (!!shippingTypesInCountry && shippingTypesInCountry.length > 0) {
            countTotal += shippingTypesInCountry.length;
        }
    }

    if (enabledShippingTypesByProvider?.length > 0) {
        for (const enabledShippingType of enabledShippingTypesByProvider) {
            const providerData = enabledShippingType.providers.find((provider) => provider.name === providerName);
            if (providerData?.shippingTypes?.length) {
                countTotal += providerData.shippingTypes.length;
            }
        }
    }
    return countTotal;
};

/**
 * If package provider has home delivery enabled.
 * Determines by home_delivery setting
 * @param providerName
 * @param homeDelivery
 */
const isHomeDeliveryEnabled = (providerName: PackageProviderName, homeDelivery?: HomeDeliverySetting): boolean => {
    if (!homeDelivery) {
        const settings = store.getState().settings.settings;
        homeDelivery = settings[SettingKey.home_delivery];
    }
    return !!homeDelivery?.[providerName]?.home_delivery;
};

/**
 * Get array of enabled package provider names.
 * Determined by provider_to_country setting.
 * @param providerToCountrySetting
 */
const getProvidersEnabled = (providerToCountrySetting?: ProviderToCountrySetting | null): PackageProviderName[] => {
    if (!providerToCountrySetting) {
        const settings = store.getState().settings.settings;
        providerToCountrySetting = settings[SettingKey.provider_to_country];
    }
    let enabled: PackageProviderName[] = [];
    for (let countryName in providerToCountrySetting) {
        if (providerToCountrySetting[countryName]?.length > 0) {
            let providersInCountry: PackageProviderName[] = providerToCountrySetting[countryName];
            enabled.push(...providersInCountry);
        }
    }
    enabled = [...new Set(enabled)];
    return enabled;
};

const isProviderHDOnly = (provider: PackageProviderName, settings?: SettingsToEcom | null) => {
    if (!settings) {
        settings = store.getState().settings.settings;
    }
    return (
        isHomeDeliveryEnabled(provider, settings?.[SettingKey.home_delivery]) &&
        countEnabledShippingTypes(provider, settings?.[SettingKey.enabled_shipping_types]) === 0
    );
};

const isProviderRateSelectRequired = (provider: PackageProviderName, settings?: SettingsToEcom | null) => {
    if (!settings) {
        settings = store.getState().settings.settings;
    }
    return countEnabledShippingTypes(provider, settings?.[SettingKey.enabled_shipping_types]) > 0;
};

/**
 * Returns true if all package providers have no shipping types enabled (enabled_shipping_types setting)
 * AND home delivery is enabled.
 * @param settings
 */
const isAllProvidersHDOnly = (settings?: SettingsToEcom | null): boolean => {
    if (!settings) {
        settings = store.getState().settings.settings;
    }
    let hdOnlyProvidersCount = 0;
    let providersEnabled = getProvidersEnabled(settings?.[SettingKey.provider_to_country]);
    for (let provider of providersEnabled) {
        if (isProviderHDOnly(provider, settings)) {
            hdOnlyProvidersCount++;
        }
    }
    return hdOnlyProvidersCount === providersEnabled.length;
};

/**
 * Get array of provider names for which at least one shipping type is enabled (on Package point tab, enabled_shipping_types setting)
 * @param settings
 */
const getProvidersWithPackagePointEnabled = (settings?: SettingsToEcom): PackageProviderName[] => {
    if (!settings) {
        settings = store.getState().settings.settings;
    }
    const providersPPEnabled: PackageProviderName[] = [];
    for (let provider of AVAILABLE_PROVIDERS) {
        if (countEnabledShippingTypes(provider, settings?.[SettingKey.enabled_shipping_types])) {
            providersPPEnabled.push(provider);
        }
    }
    return providersPPEnabled;
};

const isShippingRateSavedForProvider = (
    provider: PackageProviderName,
    shippingRatesSetting?: ShippingRatesSettings
) => {
    if (!shippingRatesSetting) {
        const settings = store.getState().settings.settings;
        shippingRatesSetting = settings[SettingKey.shipping_methods];
    }

    let saved: boolean =
        shippingRatesSetting?.[`providers`] &&
        Object.prototype.hasOwnProperty.call(shippingRatesSetting?.[`providers`], `provider_${provider}`);
    if (
        Array.isArray(shippingRatesSetting?.[`providers`]?.[`provider_${provider}`]) &&
        shippingRatesSetting?.[`providers`]?.[`provider_${provider}`].length === 0
    ) {
        saved = false;
    }
    if (
        typeof shippingRatesSetting?.[`providers`]?.[`provider_${provider}`] === 'object' &&
        Object.keys(shippingRatesSetting?.[`providers`]?.[`provider_${provider}`]).length === 0
    ) {
        saved = false;
    }
    return saved;
};

export default {
    injectStore,
    getAllCached,
    getById,
    getByName,
    getAvailableProvidersInAllOfTheCountries,
    getProvidersShippingToAllOfTheCountries,
    getAvailableProvidersInAnyOfTheCountries,
    isProviderEnabled,
    getProviderStates,
    getCountriesForProvider,
    getDisplayName,
    getAggregatorDisplayName,
    getProviderNameFromOrder,
    getCountries,
    getAvailableShippingTypes,
    countEnabledShippingTypes,
    isHomeDeliveryEnabled,
    getProvidersEnabled,
    isProviderHDOnly,
    isAllProvidersHDOnly,
    getProvidersWithPackagePointEnabled,
    isShippingRateSavedForProvider,
    isProviderRateSelectRequired,
};
